import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import { Combine, Database, Flame, Info, Leaf, Zap } from "lucide-react";
import { COLORS, GAS_UNITS, POWER_UNITS, sectionOf } from "./eaFormat";
import { useGasUnit } from "./EaContext";
import { EaPdfButton } from "./EaPdfButton";
import { EaGuideButton, EaGuidePanel } from "./EaGuide";
import { EaCurveNote } from "./EaCurveNote";

const CURVE_PAGES = ["power/panoramica", "power/consumi", "power/profili", "power/picchi", "power/heatmap", "power/confronto-pod", "power/confronto-periodi", "power/potenza", "power/pun", "power/simulazione-carichi"];

// Vademecum per pagina (Picchi e Simulazione carichi hanno il loro pannello con profili pronti).
const PAGE_GUIDE = { overview: "portfolio", "overview/budget": "budget", "power/panoramica": "overview", "gas/panoramica": "overview", "power/consumi": "consumption", "gas/consumi": "consumption",
  "power/costi": "costs", "gas/costi": "costs", "power/profili": "profiles", "power/heatmap": "heatmap", "power/confronto-pod": "compare-points", "gas/confronto-punti": "compare-points",
  "power/confronto-periodi": "compare-periods", "gas/confronto-periodi": "compare-periods", "power/potenza": "power", "power/pun": "pun", "kpi-sostenibilita/kpi": "kpi", "kpi-sostenibilita/sostenibilita": "sustainability", "gas/profili-giornalieri": "gas-daily" };

function PageGuide({ guideKey }) {
  const [open, setOpen] = useState(false);
  return <><EaGuideButton onClick={() => setOpen(true)} testid="ea-page-guide" /><EaGuidePanel guideKey={guideKey} open={open} setOpen={setOpen} /></>;
}

// Titolo su fascia colorata: colore della sezione (Totale/Power/Gas) o dell'accento; band={false} per disattivarla.
export function EaKpiCard({ label, value, sub, accent, band = true, testid, children, className = "", icon: Icon, chart }) {
  const { pathname } = useLocation();
  const bg = typeof band === "string" ? band : accent ? `linear-gradient(90deg, ${accent}, ${accent}CC)` : SECTION_STYLE[sectionOf(pathname)].bg;
  return (
    <div className={`card-elevated p-5 flex flex-col gap-2 ${band ? "overflow-hidden" : ""} ${className}`} data-testid={testid} data-pdf="kpi">
      {band ? (
        <div className="-mx-5 -mt-5 mb-1 px-5 py-2 text-xs font-semibold uppercase tracking-wider text-white flex items-center gap-2" style={{ background: bg }} data-testid={testid && `${testid}-band`} data-pdf-label>
          {Icon && <Icon size={14} strokeWidth={2.4} />}{label}
        </div>
      ) : (
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-500" data-pdf-label>
          {accent && <span className="w-2 h-2 rounded-full" style={{ background: accent }} />}
          {label}
        </div>
      )}
      <div className="flex items-end justify-between gap-3">
        <div className="min-w-0 flex flex-col gap-2">
          <div className={`font-display text-2xl font-bold tracking-tight text-slate-900 font-mono-nums ${chart ? "whitespace-nowrap" : "xl:text-3xl"}`} data-testid={testid && `${testid}-value`} data-pdf-value>
            {value}
          </div>
          {sub && <div className="text-xs text-slate-500 leading-relaxed" data-pdf-sub>{sub}</div>}
        </div>
        {chart}
      </div>
      {children}
    </div>
  );
}

// Simboli Energy Monitor: Power = fulmine, Gas = fiamma, Overview = Power + Gas.
export const SECTION_STYLE = {
  overview: { Icon: Combine, color: COLORS.total, bg: COLORS.total },
  power: { Icon: Zap, color: COLORS.power, bg: COLORS.power },
  gas: { Icon: Flame, color: COLORS.gas, bg: COLORS.gas },
  sustainability: { Icon: Leaf, color: COLORS.sustain, bg: `linear-gradient(90deg, ${COLORS.sustain}, ${COLORS.sustain}CC)` },
};


export function EaPageHeader({ title, subtitle, right, aside, units }) {
  const { pathname } = useLocation();
  const { Icon, bg } = SECTION_STYLE[sectionOf(pathname)];
  const page = pathname.replace(/^\/energy-analytics\/?/, "").replace(/\/$/, "");
  const guideKey = PAGE_GUIDE[page];
  return (
    <div className="flex items-end justify-between flex-wrap gap-4">
      <div className="flex items-start gap-3">
        <span className="w-10 h-10 rounded-xl flex items-center justify-center text-white shrink-0 mt-0.5" style={{ background: bg }} data-testid="ea-page-icon"><Icon size={20} /></span>
        <div>
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900" data-testid="ea-page-title">{title}</h1>
          {units && <div className="flex items-center gap-2 flex-wrap" data-testid="ea-page-units">{units}</div>}
        </div>
        {subtitle && <p className="text-sm text-slate-500 mt-1 max-w-2xl">{subtitle}</p>}
        </div>
      </div>
      <div className="flex flex-col items-end gap-2">
        <div className="flex items-center gap-2 flex-wrap">{guideKey && <PageGuide guideKey={guideKey} />}<EaPdfButton />{right}</div>
        {aside}
      </div>
      {CURVE_PAGES.includes(page) && <div className="w-full"><EaCurveNote pun={["power/panoramica", "power/pun", "power/potenza"].includes(page)} /></div>}
    </div>
  );
}

// Barra "Budget già speso": spesa fatturata YTD ÷ budget annuale.
export function EaBudgetBar({ spent, budget, testid, color }) {
  if (!budget || spent == null) return null;
  const pct = (spent / budget) * 100;
  return (
    <div className="mt-2" data-testid={testid}>
      <div className="flex justify-between text-[11px] text-slate-500"><span>Budget già speso</span><b className={pct > 100 ? "text-red-600" : "text-slate-700"} data-testid={`${testid}-pct`}>{pct.toFixed(1).replace(".", ",")}%</b></div>
      <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden mt-1"><div className={`h-full rounded-full ${pct > 100 ? "bg-red-500" : color ? "" : "bg-emerald-500"}`} style={{ width: `${Math.min(pct, 100)}%`, ...(pct <= 100 && color ? { background: color } : {}) }} /></div>
    </div>
  );
}

export function EaUnitToggle({ unit, setUnit, units = POWER_UNITS, prefix = "ea-unit", label, title, active = COLORS.powerDark }) {
  return (
    <div className="inline-flex items-center rounded-lg border border-slate-200 bg-white p-0.5" data-testid={`${prefix}-toggle`} title={title}>
      {label && <span className="px-2 text-[10px] font-semibold uppercase tracking-wider text-slate-400">{label}</span>}
      {Object.keys(units).map((u) => (
        <button key={u} onClick={() => setUnit(u)} data-testid={`${prefix}-${u}`}
          style={unit === u ? { background: active } : undefined}
          className={`px-3 py-1 text-xs font-semibold rounded-md transition-colors ${unit === u ? "text-white" : "text-slate-600 hover:bg-slate-100"}`}>
          {u}
        </button>
      ))}
    </div>
  );
}

export function EaGasUnitToggle({ label, prefix = "ea-gas-unit" }) {
  const g = useGasUnit();
  return <EaUnitToggle unit={g.u} setUnit={g.setU} units={GAS_UNITS} prefix={prefix} active={COLORS.gasDark} label={label} title="Conversione con PCS standard 38,1 MJ/Smc = 10,5833 kWh/Smc" />;
}

export function EaEmptyState({ title, text, testid = "ea-empty-state" }) {
  return (
    <div className="card-elevated p-10 flex flex-col items-center text-center gap-3" data-testid={testid}>
      <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center text-slate-400"><Database size={22} /></div>
      <div className="font-display text-lg font-semibold text-slate-900">{title}</div>
      {text && <div className="text-sm text-slate-500 max-w-md">{text}</div>}
    </div>
  );
}

export function EaNote({ children, testid }) {
  return (
    <div className="flex items-start gap-2 text-xs text-slate-500" data-testid={testid} data-pdf="note">
      <Info size={13} className="mt-0.5 shrink-0" /> <span>{children}</span>
    </div>
  );
}

export function EaChartCard({ title, subtitle, right, children, testid }) {
  return (
    <div className="card-elevated p-5" data-testid={testid} data-pdf="chart">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div>
          <div className="font-display font-semibold text-slate-900" data-pdf-title>{title}</div>
          {subtitle && <div className="text-xs text-slate-500 mt-0.5" data-pdf-subtitle>{subtitle}</div>}
        </div>
        {right}
      </div>
      {children}
    </div>
  );
}

export function HatchDefs({ id, color }) {
  return (
    <defs>
      <pattern id={id} patternUnits="userSpaceOnUse" width="6" height="6" patternTransform="rotate(45)">
        <rect width="6" height="6" fill={color} fillOpacity="0.18" />
        <line x1="0" y1="0" x2="0" y2="6" stroke={color} strokeWidth="2.5" strokeOpacity="0.6" />
      </pattern>
    </defs>
  );
}

export function EaSeg({ value, options, onChange, testid }) {
  return (
    <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5 flex-wrap" data-testid={testid}>
      {options.map(([v, l]) => (
        <button key={v} onClick={() => onChange(v)} data-testid={`${testid}-${v}`}
          className={`px-3 py-1 text-xs font-semibold rounded-md transition-colors ${value === v ? "bg-[#071B4D] text-white" : "text-slate-600 hover:bg-slate-100"}`}>{l}</button>
      ))}
    </div>
  );
}

export function EaField({ label, children }) {
  return (
    <label className="flex flex-col gap-1 text-xs font-semibold text-slate-500">
      {label}
      {children}
    </label>
  );
}

export const inputCls = "h-9 rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-900 font-normal";
